/**
 * StageHistoryRail — running history of every agent the reviewer has already
 * approved (or that ran and is awaiting review). Each row is collapsible and
 * shows the agent's narrative + key parsed fields PLUS the reviewer's note
 * (if any) so the next reviewer can read prior context inline without
 * leaving the page.
 *
 * Source of truth: GET /api/v1/jobs/{id}/agents (cached on JobDetailPage as
 * agentResults). The current paused stage is OMITTED from the rail — that
 * stage already renders via StageReviewPanel below it.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, MessageSquare, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { STAGE_LABELS, type PausedStage } from '@/types/enums';
import { fmtDateTime, fmtDurationBetween } from '@/lib/datetime';
import {
  extractReviewerNote,
  summaryBitsFor,
} from './StageHistoryRowHelpers';
import { StatusBadge } from './StageStatusBadge';
import { ReviewerNoteCallout } from './ReviewerNoteCallout';
import { GateGroupSummary } from './GateGroupSummary';
import { GateNotesRollup } from './GateNotesRollup';
import { GateWarningsResume } from './GateWarningsResume';
import { stageRole } from './gateGroups';
import type { AgentResult } from '@/types';

interface Props {
  agentResults: AgentResult[];
  currentPausedStage: PausedStage | null;
}

const STAGE_KEYS: PausedStage[] = [
  'document_assistant',
  'document',
  'parties',
  'clause',
  'gate_1',
  'compliance',
  'signature',
  'gate_2',
  'gate',
  'orchestrator_bootstrap',
];

function HistoryRow({ result, agents }: { result: AgentResult; agents: AgentResult[] }) {
  const [open, setOpen] = useState(false);
  const name = result.agent_name;
  const isGate = stageRole(name) === 'intermediate_gate';
  const knownStage = (STAGE_KEYS as readonly string[]).includes(name);
  const label = knownStage ? STAGE_LABELS[name as PausedStage] : name;
  const narrative =
    typeof result.output?.narrative === 'string' ? result.output.narrative : '';
  const summaryBits = summaryBitsFor(name, result.output);
  const reviewerNote = extractReviewerNote(result.edits_json ?? null);
  const noteAt = result.approved_at;

  return (
    <Card className="border-border">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); }}
        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-muted/50 rounded-t-lg"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground mt-1" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground mt-1" aria-hidden="true" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{label}</span>
            {summaryBits.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {summaryBits.join(' · ')}
              </span>
            )}
          </div>
          {reviewerNote && (
            <p className="mt-0.5 truncate text-xs italic text-violet-700 dark:text-violet-300"
               title={reviewerNote}>
              <MessageSquare className="inline h-3 w-3 mr-1 align-[-2px]" aria-hidden="true" />
              {reviewerNote.length > 100 ? `${reviewerNote.slice(0, 100)}…` : reviewerNote}
            </p>
          )}
        </div>
        <span className="flex items-center gap-2 mt-1">
          <span className="text-[10px] font-mono text-muted-foreground hidden sm:inline">
            {result.started_at && fmtDateTime(result.started_at)}
            {result.completed_at && result.started_at && ' → '}
            {result.completed_at && fmtDateTime(result.completed_at)}
            {fmtDurationBetween(result.started_at, result.completed_at) &&
              ` · ${fmtDurationBetween(result.started_at, result.completed_at)}`}
          </span>
          <StatusBadge result={result} />
        </span>
      </button>
      {open && (
        <CardContent className="pt-2 pb-3 px-3 space-y-2 text-sm border-t">
          {isGate && (
            <div className="space-y-2">
              <GateGroupSummary gate={name} agents={agents} />
              <GateWarningsResume gate={name} agents={agents} />
              <GateNotesRollup gate={name} agents={agents} />
            </div>
          )}
          {reviewerNote && <ReviewerNoteCallout note={reviewerNote} at={noteAt} />}
          {result.error_message && (
            <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-950/30 dark:text-red-300">
              <span className="font-medium">Agent error:</span> {result.error_message}
            </div>
          )}
          {narrative && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <Pencil className="h-3 w-3" aria-hidden="true" />
                Agent report
              </p>
              <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed rounded bg-muted/40 p-2 max-h-80 overflow-auto">
                {narrative}
              </pre>
            </div>
          )}
          {!narrative && result.output && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Show full output JSON
              </summary>
              <pre className="mt-1 text-[11px] whitespace-pre-wrap rounded bg-muted/40 p-2 max-h-72 overflow-auto">
                {JSON.stringify(result.output, null, 2)}
              </pre>
            </details>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function StageHistoryRail({ agentResults, currentPausedStage }: Props) {
  // Take ONE row per stage (most recent attempt), in canonical order.
  const byStage = new Map<string, AgentResult>();
  for (const r of agentResults) {
    const prev = byStage.get(r.agent_name);
    if (!prev || prev.attempt < r.attempt) byStage.set(r.agent_name, r);
  }
  const ordered: AgentResult[] = STAGE_KEYS
    .filter((s) => s !== currentPausedStage)
    .map((s) => byStage.get(s))
    .filter((r): r is AgentResult => Boolean(r));

  if (ordered.length === 0) return null;

  return (
    <section aria-labelledby="stage-history-heading" className="space-y-2">
      <h2 id="stage-history-heading" className="text-sm font-semibold text-muted-foreground">
        Stage history — previously approved
      </h2>
      <div className="space-y-2">
        {ordered.map((r) => (
          <HistoryRow key={r.id} result={r} agents={agentResults} />
        ))}
      </div>
    </section>
  );
}
